/**
 * Section & Table Service
 * Provides Supabase calls for outlet section and table management
 */

import { ApiResponse } from '../../shared/types';
import {
  CreateSectionRequest,
  CreateTableRequest,
  OutletSection,
  OutletTable,
  UpdateSectionRequest,
  UpdateTableRequest,
} from '../types/section';
import { supabase } from './supabase';

// ===== SECTIONS =====

/**
 * Fetch all sections for a given outlet
 */
export const fetchOutletSections = async (
  outletId?: string | null,
): Promise<ApiResponse<OutletSection[]>> => {
  try {
    let query = supabase
      .from('outlet_sections_view')
      .select('*')
      .order('name', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    // Map view data to OutletSection type
    const sections: OutletSection[] = (data || []).map((section: any) => ({
      id: section.id,
      name: section.name,
      outlet_id: section.outlet_id,
      outlet_name: section.outlet_name || '',
      company_id: section.company_id || undefined,
      is_hidden: section.is_hidden,
      created_at: section.created_at,
      updated_at: section.updated_at,
      table_count: section.table_count || 0,
    }));

    return { success: true, data: sections };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch sections',
      data: [],
    };
  }
};

/**
 * Create a new section
 */
export const createSection = async (
  request: CreateSectionRequest,
): Promise<ApiResponse<OutletSection>> => {
  try {
    const { data, error } = await supabase
      .from('outlet_sections')
      .insert({
        name: request.name.toUpperCase(),
        outlet_id: request.outlet_id,
        is_hidden: request.is_hidden ?? false,
      })
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: { ...data, table_count: 0 } as OutletSection,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to create section',
      data: undefined,
    };
  }
};

/**
 * Update an existing section
 */
export const updateSection = async (
  request: UpdateSectionRequest,
): Promise<ApiResponse<OutletSection>> => {
  try {
    const updateData: Partial<OutletSection> = {};
    if (request.name !== undefined) updateData.name = request.name;
    if (request.is_hidden !== undefined)
      updateData.is_hidden = request.is_hidden;

    const { data, error } = await supabase
      .from('outlet_sections')
      .update(updateData)
      .eq('id', request.id)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: data as OutletSection };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to update section',
      data: undefined,
    };
  }
};

// ===== TABLES =====

/**
 * Fetch all tables for a given outlet (across all sections)
 */
export const fetchOutletTables = async (
  outletId?: string | null,
): Promise<ApiResponse<OutletTable[]>> => {
  try {
    let query = supabase
      .from('outlet_tables_view')
      .select('*')
      .order('name', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    // Map view data to OutletTable type (including optional running-order info).
    const tables: OutletTable[] = (data || []).map((table: any) => ({
      bill_number:
        table.bill_number ??
        table.current_bill_number ??
        table.running_order_bill_number ??
        table.current_order_bill_number ??
        table.active_bill_number ??
        null,
      has_running_order: Boolean(
        table.has_running_order ??
        table.has_active_order ??
        table.bill_number ??
        table.current_bill_number ??
        table.running_order_bill_number ??
        table.current_order_bill_number ??
        table.active_bill_number,
      ),
      id: table.id,
      name: table.name,
      section_id: table.section_id,
      outlet_id: table.outlet_id,
      outlet_name: table.outlet_name || '',
      company_id: table.company_id || undefined,
      is_hidden: table.is_hidden,
      created_at: table.created_at,
      updated_at: table.updated_at,
      section_name: table.section_name || '',
    }));

    return { success: true, data: tables };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch tables',
      data: [],
    };
  }
};

/**
 * Create a new table
 */
export const createTable = async (
  request: CreateTableRequest,
): Promise<ApiResponse<OutletTable>> => {
  try {
    const { data, error } = await supabase
      .from('outlet_tables')
      .insert({
        name: request.name.toUpperCase(),
        section_id: request.section_id,
        is_hidden: request.is_hidden ?? false,
      })
      .select('*, outlet_sections(name)')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: {
        bill_number: null,
        has_running_order: false,
        id: data.id,
        name: data.name,
        section_id: data.section_id,
        is_hidden: data.is_hidden,
        created_at: data.created_at,
        updated_at: data.updated_at,
        section_name: (data as any).outlet_sections?.name || '',
      } as OutletTable,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create table',
      data: undefined,
    };
  }
};

/**
 * Update an existing table
 */
export const updateTable = async (
  request: UpdateTableRequest,
): Promise<ApiResponse<OutletTable>> => {
  try {
    const updateData: Partial<OutletTable> = {};
    if (request.name !== undefined) updateData.name = request.name;
    if (request.is_hidden !== undefined)
      updateData.is_hidden = request.is_hidden;

    const { data, error } = await supabase
      .from('outlet_tables')
      .update(updateData)
      .eq('id', request.id)
      .select('*, outlet_sections(name)')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: {
        bill_number: null,
        has_running_order: false,
        id: data.id,
        name: data.name,
        section_id: data.section_id,
        is_hidden: data.is_hidden,
        created_at: data.created_at,
        updated_at: data.updated_at,
        section_name: (data as any).outlet_sections?.name || '',
      } as OutletTable,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update table',
      data: undefined,
    };
  }
};
